import React, { FC, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface PostIFMarkEmployeeDialogProps {
  open: boolean;
  applicantName?: string;
  /**
   * The employee number already on the record, for an existing employee or a rehire (the identity
   * lookup captured it at draft save). Prefilled so HR CONFIRMS a known-correct, system-derived id
   * rather than retyping it — mark-as-employee overwrites the stored value, so a typo here would
   * silently replace a good id with a bad one. Undefined for a brand-new hire, who has no number
   * until HR issues one.
   */
  defaultEmployeeId?: number;
  /** The mark request is in flight - Confirm (and Enter) are blocked so a double-click can't send it twice. */
  confirming?: boolean;
  onClose: () => void;
  onConfirm: (enterpriseEmployeeId: number) => void;
}

// HR converts an approved PostIF into an employee record by linking its Enterprise employee number.
const PostIFMarkEmployeeDialog: FC<PostIFMarkEmployeeDialogProps> = ({
  open,
  applicantName,
  defaultEmployeeId,
  confirming = false,
  onClose,
  onConfirm,
}) => {
  const [employeeNo, setEmployeeNo] = useState("");

  // Re-seed the field whenever the dialog reopens, so it never carries the previous row's number.
  useEffect(() => {
    if (open) setEmployeeNo(defaultEmployeeId ? String(defaultEmployeeId) : "");
  }, [open, defaultEmployeeId]);

  const parsed = Number(employeeNo);
  const isValid = employeeNo.trim() !== "" && Number.isInteger(parsed) && parsed > 0;

  const handleConfirm = () => {
    if (isValid && !confirming) onConfirm(parsed);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="postif-mark-employee-title"
      PaperProps={{ className: "confirmation-dialog-paper" }}
    >
      <DialogTitle id="postif-mark-employee-title">Mark as employee</DialogTitle>
      <IconButton aria-label="close" onClick={onClose} className="dialog-close-button">
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography gutterBottom>
          {applicantName
            ? `Link ${applicantName}'s Post Interview Form to their employee record.`
            : "Link this Post Interview Form to the employee record."}{" "}
          Enter the Enterprise employee number.
        </Typography>
        <TextField
          // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: the modal's single input
          autoFocus
          fullWidth
          size="small"
          label="Employee #"
          value={employeeNo}
          onChange={(e) => setEmployeeNo(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          helperText={
            defaultEmployeeId
              ? "Taken from the form they are already an employee. Change it only if it is wrong."
              : undefined
          }
          inputProps={{ inputMode: "numeric", "data-testid": "postif-mark-employee-input" }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="light"
          size="small"
          onClick={onClose}
          data-testid="postif-mark-employee-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!isValid || confirming}
          onClick={handleConfirm}
          data-testid="postif-mark-employee-confirm"
        >
          Mark as employee
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostIFMarkEmployeeDialog;
